import { useEffect, useState } from 'react';
import { Input, Modal, Radio, Select, Typography } from 'antd';
import type { Connection } from '@xyflow/react';
import type { EdgeKind } from '@/flow-ir/types';

const { Text } = Typography;

export interface EdgeDraftExtra {
  onStatus?: string;
  guard?: string;
  binding?: { source: string; target: string };
}

/**
 * The connect-time kind picker (spec §4.5 / interaction #3). Enforces the 3-kind discipline: DATA
 * offers ONLY a binding (it can never gate); STATUS requires a status token; SEQUENCE may carry an
 * optional guard. The default kind is inferred from which handles the author connected.
 */
export function EdgeKindPicker({
  connection,
  defaultKind,
  statusOptions,
  onConfirm,
  onCancel,
}: {
  connection: Connection | null;
  defaultKind: EdgeKind;
  statusOptions: string[];
  onConfirm: (kind: EdgeKind, extra: EdgeDraftExtra) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<EdgeKind>(defaultKind);
  const [onStatus, setOnStatus] = useState<string>('');
  const [guard, setGuard] = useState<string>('');
  const [bindSource, setBindSource] = useState<string>('');
  const [bindTarget, setBindTarget] = useState<string>('');

  useEffect(() => {
    if (connection) {
      setKind(defaultKind);
      setOnStatus('');
      setGuard('');
      setBindSource('');
      setBindTarget('');
    }
  }, [connection, defaultKind]);

  const okDisabled =
    (kind === 'status' && !onStatus.trim()) ||
    (kind === 'data' && (!bindSource.trim() || !bindTarget.trim()));

  const confirm = () => {
    if (kind === 'status') onConfirm('status', { onStatus: onStatus.trim() });
    else if (kind === 'data') onConfirm('data', { binding: { source: bindSource.trim(), target: bindTarget.trim() } });
    else onConfirm('sequence', { guard: guard.trim() || undefined });
  };

  return (
    <Modal
      open={Boolean(connection)}
      title="Connect nodes — choose edge kind"
      okText="Add edge"
      okButtonProps={{ disabled: okDisabled }}
      onOk={confirm}
      onCancel={onCancel}
      width={440}
      destroyOnClose
    >
      {connection && (
        <div>
          <Text type="secondary" className="ol-mono" style={{ fontSize: 12 }}>
            {connection.source} → {connection.target}
          </Text>
          <div style={{ margin: '14px 0 6px' }}>
            <Radio.Group value={kind} onChange={(e) => setKind(e.target.value)} optionType="button" buttonStyle="solid">
              <Radio.Button value="sequence">Sequence</Radio.Button>
              <Radio.Button value="status">Status</Radio.Button>
              <Radio.Button value="data">Data</Radio.Button>
            </Radio.Group>
          </div>

          {kind === 'sequence' && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                B starts after A completes. Optional CEL guard:
              </Text>
              <Input
                className="ol-mono"
                size="small"
                style={{ marginTop: 6 }}
                placeholder="input.amount > 100000"
                value={guard}
                onChange={(e) => setGuard(e.target.value)}
              />
            </div>
          )}

          {kind === 'status' && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                Routes when the source completes with this status (closed vocabulary):
              </Text>
              <Select
                size="small"
                mode="tags"
                maxCount={1}
                style={{ width: '100%', marginTop: 6 }}
                placeholder="e.g. FAILED"
                value={onStatus ? [onStatus] : []}
                options={statusOptions.map((s) => ({ value: s, label: s }))}
                onChange={(v) => setOnStatus((v as string[])[0] ?? '')}
              />
            </div>
          )}

          {kind === 'data' && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                Binds an output to an input. A data edge NEVER gates execution.
              </Text>
              <Input
                className="ol-mono"
                size="small"
                style={{ marginTop: 6 }}
                placeholder="source output — e.g. pan.name"
                value={bindSource}
                onChange={(e) => setBindSource(e.target.value)}
              />
              <Input
                className="ol-mono"
                size="small"
                style={{ marginTop: 6 }}
                placeholder="target input — e.g. applicant.name"
                value={bindTarget}
                onChange={(e) => setBindTarget(e.target.value)}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
